import React from 'react';
import { Link } from 'react-router-dom';
import { ThemeProvider, styled } from 'styled-components';

import { FooterSection } from './FooterSection';
import { Header } from './Header';
import { PageDescription } from './PageDescription';
import { PageTitle } from './PageTitle';
import { PageWrapper } from './PageWrapper';
import { StandardSection, StandardSectionContentContainer } from './StandardSection';
import { theme } from '../theme/aikya';
import guides from '../data/guides.json';
import { formatGuideDate } from '../utils/formatGuideDate';

const GuidesContent = styled(StandardSectionContentContainer)`
	box-sizing: border-box;
	margin: 0 auto;
	max-width: 56rem;
	width: 100%;
`;

const GuidesDescription = styled(PageDescription)`
	margin-bottom: 1rem;
	text-wrap: balance;
`;

const GuideList = styled.div`
	max-width: 52rem;
`;

const GuideEntry = styled(Link)`
	align-items: center;
	box-sizing: border-box;
	color: ${(props) => props.theme.colors.neutral1};
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	min-height: 7rem;
	margin: 0 -1.5rem;
	padding: 1rem 1.5rem;
	text-decoration: none;

	&:hover,
	&:focus-visible {
		color: ${(props) => props.theme.colors.purple4};
		background-color: ${(props) => props.theme.colors.purple9};
	}

	&:hover p,
	&:hover time,
	&:focus-visible p,
	&:focus-visible time {
		color: ${(props) => props.theme.colors.purple4};
	}

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.second}) {
		border-radius: 0.5rem;
		flex-direction: row;
		margin-left: -1rem;
		margin-right: -1rem;
		padding-left: 1rem;
		padding-right: 1rem;
	}
`;

const GuideThumbnail = styled.img`
	aspect-ratio: 16 / 9;
	border-radius: 0.5rem;
	flex: 0 0 8rem;
	object-fit: cover;
	width: 100%;

	@media only screen and (min-width: ${(props) => props.theme.breakpoints.second}) {
		width: 16rem;
	}
`;

const GuideEntryText = styled.div`
	flex: 1;
`;

const GuideEntryTitle = styled.h2`
	${(props) => props.theme.typography.headingS};
	margin: 0 0 0.5rem;
`;

const GuideEntryDescription = styled.p`
	${(props) => props.theme.typography.bodyXS};
	color: ${(props) => props.theme.colors.neutral4};
	margin: 0;
	text-wrap: balance;
`;

const GuideLastUpdated = styled.time`
	${(props) => props.theme.typography.body2XS};
	color: ${(props) => props.theme.colors.neutral4};
	display: block;
	margin-top: 0.35rem;
`;

export const GuidesPage = () => (
	<ThemeProvider theme={theme}>
		<PageWrapper>
			<Header />
			<StandardSection>
				<GuidesContent>
					<PageTitle>Guides</PageTitle>
					<GuidesDescription>Helpful tutorials and resources for FPV hobbyists.</GuidesDescription>
					<GuideList>
						{guides.filter((guide) => guide.public).map((guide) => (
							<GuideEntry key={guide.slug} to={`/guides/${guide.slug}`}>
								{guide.image && <GuideThumbnail src={guide.image} alt={guide.imageAlt} />}
								<GuideEntryText>
									<GuideEntryTitle>{guide.title}</GuideEntryTitle>
									<GuideEntryDescription>{guide.description}</GuideEntryDescription>
									<GuideLastUpdated dateTime={guide.lastUpdated}>
										Last updated: {formatGuideDate(guide.lastUpdated)}
									</GuideLastUpdated>
								</GuideEntryText>
							</GuideEntry>
						))}
					</GuideList>
				</GuidesContent>
			</StandardSection>
			<FooterSection />
		</PageWrapper>
	</ThemeProvider>
);